import { ApolloLink } from '@apollo/client'
import { errorTracking } from 'utils/posthog'

export interface PageSizeLimitConfig {
  /** Hard cap applied to the configured pagination variables. */
  maxPageSize: number
  /** Operation name → pagination variable names to clamp (e.g. first/limit). */
  operations: Record<string, readonly string[]>
}

/**
 * Clamps pagination variables to the backend's max page size before the
 * request leaves the client. The backend caps list queries, so a single
 * oversized `first`/`limit` takes the whole screen down; clamping at the
 * link layer guarantees no call site can exceed the cap. Every clamp is
 * reported to PostHog (fingerprint `pageSizeClamped:<operation>`) so the
 * offending call site surfaces instead of failing silently.
 */
export const createPageSizeLimitLink = (
  config: PageSizeLimitConfig
): ApolloLink =>
  new ApolloLink((operation, forward) => {
    const variableNames = config.operations[operation.operationName] ?? []

    for (const name of variableNames) {
      const requested = operation.variables?.[name]
      if (typeof requested !== 'number' || requested <= config.maxPageSize) {
        continue
      }

      operation.variables[name] = config.maxPageSize

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          `[pageSizeLimitLink] ${operation.operationName}.$${name}=${requested} exceeds the backend max of ${config.maxPageSize} — clamped. Paginate or chunk the request instead.`
        )
      }

      errorTracking({
        error: new Error(
          `Clamped ${operation.operationName}.$${name} from ${requested} to ${config.maxPageSize}`
        ),
        properties: {
          operation: operation.operationName,
          variable: name,
          requested
        },
        fingerprint: 'pageSizeClamped'
      })
    }

    return forward(operation)
  })
